import React, { ReactElement, useCallback, useMemo, useRef } from 'react';

import { DiagonalHatchDefinition, LengthIndicator } from '@da-g3-acl/shared/common';

import { rulerIsUnallocated, RulerSegment } from '../../../../helpers/image-to-ruler-allocation';
import { isYIQDark } from '../../../../types/colour';
import { LANE_RULER_HEIGHT_PX, LANE_RULER_SCALE_THRESHOLD } from '../../../../types/constants';
import { LengthRange } from '../../../../types/lengthRange';
import { CoreLaneRowAlignment, CoreLaneRowType, RulerLaneRowConfiguration } from '../configurations';
import { LaneRowPropsBase } from '../types';

import HorizontalTicks from './HorizontalTicks';

export interface RulerLaneRowProps extends LaneRowPropsBase<RulerLaneRowConfiguration> {}

// eslint-disable-next-line react-refresh/only-export-components
export const DefaultRulerCfg: RulerLaneRowConfiguration = {
	id: 'ruler',
	type: CoreLaneRowType.Ruler,
	alignment: CoreLaneRowAlignment.Bottom,
	thickness: { fixedPx: LANE_RULER_HEIGHT_PX, threshold: LANE_RULER_SCALE_THRESHOLD },
	isLoading: false,
};

/**
 * Take an input segment, and trim it so that it fits within the range. If the interval does not fit at all, then returns undefined
 * @param {RulerSegment} segment The segment to trim
 * @param {LengthRange} range The range to trim the segment to
 * @returns {RulerSegment} The trimmed ruler segment
 */
const getTrimmedSegment = (segment: RulerSegment, range: LengthRange): RulerSegment | undefined => {
	// Segment is wholly excluded
	if (segment.renderableDepth.depthTo <= range.depthFrom || segment.renderableDepth.depthFrom >= range.depthTo)
		return undefined;
	// Segment has no trimming
	if (segment.renderableDepth.depthFrom >= range.depthFrom && segment.renderableDepth.depthTo <= range.depthTo)
		return segment;
	// Trim the segment.
	// Note, that the tray and downhole ranges may not be the same length, so must calculate a ratio
	const from = Math.max(range.depthFrom, segment.renderableDepth.depthFrom);
	const to = Math.min(range.depthTo, segment.renderableDepth.depthTo);
	const fromRatio =
		(from - segment.renderableDepth.depthFrom) /
		(segment.renderableDepth.depthTo - segment.renderableDepth.depthFrom);
	const toRatio =
		(to - segment.renderableDepth.depthFrom) /
		(segment.renderableDepth.depthTo - segment.renderableDepth.depthFrom);
	return {
		...segment,
		renderableDepth: {
			depthFrom: from,
			depthTo: to,
		},
		rulerDepth: {
			depthFrom:
				segment.rulerDepth.depthFrom + (segment.rulerDepth.depthTo - segment.rulerDepth.depthFrom) * fromRatio,
			depthTo:
				segment.rulerDepth.depthFrom + (segment.rulerDepth.depthTo - segment.rulerDepth.depthFrom) * toRatio,
		},
	};
};

/**
 * A component that fills an entire row with allocated ruler segments. These segments are provided by the configuration,
 * and can allocate non-linear rulers, as well as omit any rulers at all
 * @constructor
 */
export const RulerLaneRow = (props: RulerLaneRowProps): JSX.Element => {
	const { position, lane, scale, heightInPx, configuration, width } = props;
	const { indicators, onIndicatorHover, onSegmentHover, segments } = configuration;

	const onHorizontalTicksIndicatorHover = useCallback(
		(l: LengthIndicator | undefined) => {
			return onIndicatorHover && onIndicatorHover(l);
		},
		[onIndicatorHover],
	);

	const visibleSegments: RulerSegment[] = useMemo(
		() =>
			segments
				?.filter(
					(s) =>
						(s.renderableDepth.depthFrom <= lane.depthFrom &&
							s.renderableDepth.depthTo >= lane.depthFrom) ||
						(s.renderableDepth.depthFrom <= lane.depthTo && s.renderableDepth.depthTo >= lane.depthTo) ||
						(s.renderableDepth.depthFrom <= lane.depthFrom && s.renderableDepth.depthTo >= lane.depthTo) ||
						(s.renderableDepth.depthFrom >= lane.depthFrom && s.renderableDepth.depthTo <= lane.depthTo),
				)
				.map((s) => getTrimmedSegment(s, lane))
				.reduce<RulerSegment[]>((prev, curr) => (curr ? [...prev, curr] : prev), []) ?? [
				{ renderableDepth: lane, rulerDepth: lane },
			],
		[segments, lane],
	);

	const labelHeightInPx = useMemo(() => {
		return Math.min(heightInPx - 2, 16);
	}, [heightInPx]);

	const measureLabelWidth = useCallback(
		(text: string) => {
			const canvas = document.createElement('canvas');
			const context = canvas.getContext('2d');
			if (!context) return 0;
			context.font = `${labelHeightInPx}px sans-serif`;
			return context.measureText(text).width;
		},
		[labelHeightInPx],
	);

	const renderRulerSegmentFn = useCallback(
		(segment: RulerSegment, x: number): ReactElement | undefined => {
			if (!rulerIsUnallocated(segment))
				return (
					<foreignObject
						x={x}
						y={0}
						height={heightInPx}
						width={'100%'}
						key={`ruler-segment-${lane.depthFrom}-${segment.renderableDepth.depthFrom}`}
					>
						<HorizontalTicks
							widthInPx={(segment.rulerDepth.depthTo - segment.rulerDepth.depthFrom) * scale}
							range={segment.rulerDepth}
							lengthIndicators={indicators}
							onIndicatorHover={(h) => onHorizontalTicksIndicatorHover(h)}
						/>
					</foreignObject>
				);
			return undefined;
		},
		[heightInPx, indicators, onHorizontalTicksIndicatorHover, scale, lane.depthFrom],
	);

	const overlapRenderFn = useCallback(
		(segment: RulerSegment, x: number): ReactElement | undefined => {
			const length = (segment.renderableDepth.depthTo - segment.renderableDepth.depthFrom) * scale;
			const labelX = x + length / 2;

			const labelIsWhite = isYIQDark(segment.colour ?? '#888888');

			return (
				<g key={`ruler-segment-${segment.renderableDepth.depthFrom}`}>
					<rect
						x={x}
						width={(segment.renderableDepth.depthTo - segment.renderableDepth.depthFrom) * scale}
						y={0}
						height={heightInPx}
						fill={segment.colour}
					/>
					<rect x={x} y={0} width={length} height={heightInPx} fill="url(#diagonalHatch)" />
					{labelX > 0 && segment.label && measureLabelWidth(segment.label) < length - 16 && (
						<text
							x={labelX}
							y={heightInPx / 2}
							style={{
								fill: labelIsWhite ? '#fff' : '#000',
								fontSize: labelHeightInPx,
							}}
							width={length}
							dominantBaseline="middle"
							textAnchor="middle"
						>
							{segment.label}
						</text>
					)}
					<rect
						x={x}
						width={(segment.renderableDepth.depthTo - segment.renderableDepth.depthFrom) * scale}
						y={0}
						height={heightInPx}
						fill={'white'}
						fillOpacity={0}
						onMouseEnter={() => onSegmentHover?.(segment)}
						onMouseLeave={() => onSegmentHover?.(undefined)}
					/>
				</g>
			);
		},
		[heightInPx, labelHeightInPx, measureLabelWidth, scale, onSegmentHover],
	);

	const visibleSegmentRenderFn = useCallback(
		(segment: RulerSegment): ReactElement | undefined => {
			const x = Math.max(0, (segment.renderableDepth.depthFrom - lane.depthFrom) * scale);
			if (rulerIsUnallocated(segment)) return overlapRenderFn(segment, x);
			else return renderRulerSegmentFn(segment, x);
		},
		[lane.depthFrom, overlapRenderFn, renderRulerSegmentFn, scale],
	);

	const svgRef = useRef<SVGSVGElement>(null);

	return (
		<svg ref={svgRef} width={width} height={heightInPx} x={0} y={position} style={{ cursor: 'default' }}>
			<defs>
				<DiagonalHatchDefinition id="diagonalHatch" />
			</defs>
			<rect x={0} y={0} height={heightInPx} width={'100%'} fill="white" />
			{/**
			 * Insert <foreignObject> + <g> group for "log indicators"
			 * Potential approach:
			 * - Pass Collection of ranges (inc colour) into viewer
			 * - Calculate which row ranges falls into (in use-stacked-rows)
			 * 	- Add "loggedRanges" to StackedRow
			 * 	- Split over multiple rows if necessary
			 * - Draw ranges for row into <g> group as rects:
			 * 	- Calculate xStart/xEnd
			 **/}
			{visibleSegments.map((s) => visibleSegmentRenderFn(s))}
		</svg>
	);
};
